import useSWR from "swr";
import { ProductSummary } from "@/common/productSummary/types";
import { ProductSearchListItemProps } from "../components/v2/ProductSearchListItem";
import getProductSummaryFlexibility from "@/lib/productSummary/helper/getProductSummaryFlexibility";
import { normalizePartnerDisplayName } from "@/utils/functions/normalizePartnerDisplayName";
import {
  analyticsSkillsFromProductSummary,
  formatAnalyticsDurationWeeks,
  formatAnalyticsListField,
} from "./analyticsFormatters";

const convertProductSummaryToSearchListItem = (
  product: ProductSummary,
): ProductSearchListItemProps => {
  const partnerKey = product.partnerKeys?.[0] ?? "";
  const partnerFullName = normalizePartnerDisplayName(
    product.partnerName,
    partnerKey,
  );

  const flex = getProductSummaryFlexibility(product.flexibility);
  const skillsDisplay = analyticsSkillsFromProductSummary(product);
  const lvl = formatAnalyticsListField(product.level);

  return {
    id: product.productUuid,
    productName: product.productName,
    productCategory:
      product.productCategory ??
      product.attributes?.[0] ??
      String(product.productType),
    partner: {
      key: partnerKey,
      fullName: partnerFullName,
    },
    partnerLogoUrl: product.partnerLogoOverride ?? product.partnerLogoUrl,
    timeToComplete: product.weeksToComplete ?? undefined,
    href: `/${product.productSlug}`,
    variant: (product.product ?? String(product.productType)) as string,
    analyticsDurationDisplay: formatAnalyticsDurationWeeks(
      product.weeksToComplete,
    ),
    analyticsLevel: lvl !== "" ? lvl : undefined,
    analyticsFlexibility: flex ? String(flex) : undefined,
    analyticsSkillsDisplay: skillsDisplay !== "" ? skillsDisplay : undefined,
  };
};

const fetchProductSummaries = async (
  uuids: string[],
): Promise<ProductSummary[]> => {
  if (uuids.length === 0) return [];

  const response = await fetch("/api/product-summaries", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ uuids }),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch product summaries");
  }

  return response.json();
};

interface BeforeTypingProducts {
  featuredPrograms: ProductSearchListItemProps[];
  isLoading: boolean;
  error: Error | undefined;
}

export const useBeforeTypingProducts = (
  featuredProgramsUUIDs: string[],
): BeforeTypingProducts => {
  const uniqueUUIDs = Array.from(new Set(featuredProgramsUUIDs)).slice(0, 5);

  const {
    data: products,
    error,
    isLoading,
  } = useSWR(
    uniqueUUIDs.length > 0 ? ["api/product-summaries", ...uniqueUUIDs] : null,
    () => fetchProductSummaries(uniqueUUIDs),
  );

  if (!products) {
    return { featuredPrograms: [], isLoading, error };
  }

  const uuidSet = new Set(uniqueUUIDs);
  const featuredPrograms: ProductSearchListItemProps[] = [];
  const seen = new Set<string>();

  for (const product of products) {
    if (seen.has(product.productUuid)) continue;
    if (!uuidSet.has(product.productUuid)) continue;

    featuredPrograms.push(convertProductSummaryToSearchListItem(product));
    seen.add(product.productUuid);
  }

  return { featuredPrograms, isLoading, error };
};
